import { Cancel } from "@mui/icons-material";
import { Box, Chip, ChipDelete, Stack } from "@mui/joy";

import shortDateFormat from "@/utils/shortDateFormat";

import { useFilterParams, useRoutesFacets, useRoutesTableStore } from "./RoutesProvider2";
import {
  FilterParams,
  RangeFilter,
  getFilterLabel,
  getRangeFilterChoices,
  isMultipleChoiceFilter,
  isRangeFilter,
  isRangeFilterName,
  RangeFilterName,
} from "./useFilteredRoutes";

const getRangeFilterSummaryText = (
  filterName: RangeFilterName,
  filterParamValue: RangeFilter<number> | RangeFilter<string>,
) => {
  const choices = getRangeFilterChoices(filterName);
  let minOnlyPrefix = "at least ";
  let maxOnlyPrefix = "up to ";
  let valueSuffix = "";
  const isDateRange = filterName === "startTime" || filterName === "endTime";

  if (filterName === "timeLeft") {
    minOnlyPrefix = "over ";
    maxOnlyPrefix = "under ";
  }
  if (filterName === "age") {
    minOnlyPrefix = "over ";
    maxOnlyPrefix = "under ";
    valueSuffix = " old";
  }
  if (isDateRange) {
    minOnlyPrefix = "after ";
    maxOnlyPrefix = "before ";
    valueSuffix = "";
  }

  let minChoiceLabel = choices
    ? choices?.find((choice) => choice.value === filterParamValue.min)?.label
    : filterParamValue.min;
  let maxChoiceLabel = choices
    ? choices.find((choice) => choice.value === filterParamValue.max)?.label
    : filterParamValue.max;

  if (isDateRange) {
    minChoiceLabel =
      filterParamValue.min && shortDateFormat(new Date(filterParamValue.min as string));
    maxChoiceLabel =
      filterParamValue.max && shortDateFormat(new Date(filterParamValue.max as string));
  }

  let summaryText;
  if (filterParamValue.min === undefined && filterParamValue.max === undefined) {
    summaryText = "Any";
  }
  if (filterParamValue.min === filterParamValue.max && filterParamValue.min !== undefined) {
    summaryText = minChoiceLabel?.toString();
  } else if (filterParamValue.min === undefined && filterParamValue.max !== undefined) {
    summaryText = `${maxOnlyPrefix}${maxChoiceLabel?.toString()}${valueSuffix}`;
  } else if (filterParamValue.max === undefined && filterParamValue.min !== undefined) {
    summaryText = `${minOnlyPrefix}${minChoiceLabel?.toString()}${valueSuffix}`;
  } else if (filterParamValue.min !== undefined && filterParamValue.max !== undefined) {
    summaryText = `${minChoiceLabel?.toString()} - ${maxChoiceLabel?.toString()}${valueSuffix}`;
  }
  return summaryText;
};

const FilterIndicator = ({
  filterName,
  filterParamValue,
}: {
  filterName: keyof FilterParams;
  filterParamValue: FilterParams[keyof FilterParams];
}) => {
  const label = getFilterLabel(filterName);

  const removeFilterParam = useRoutesTableStore((state) => state.removeFilterParam);
  const routesFacets = useRoutesFacets();

  let summaryText;
  let onlySummary = false;
  if (isMultipleChoiceFilter(filterParamValue)) {
    const facetValues = routesFacets?.[filterName as keyof typeof routesFacets];
    summaryText = Array.from(filterParamValue.matches)
      .slice(0, 3)
      // TODO: fix type
      // oxlint-disable-next-line typescript/ban-ts-comment
      // @ts-ignore
      .map((value) => facetValues?.get?.(value)?.name)
      .join(", ");
    if (filterParamValue.matches.size > 3) {
      summaryText += `, +${filterParamValue.matches.size - 3} more`;
    }
  } else if (isRangeFilter(filterParamValue) && isRangeFilterName(filterName)) {
    summaryText = getRangeFilterSummaryText(filterName, filterParamValue);
  } else if (typeof filterParamValue === "boolean") {
    onlySummary = true;
    summaryText = getFilterLabel(filterName);
  }

  let summary = <strong>{summaryText}</strong>;

  if (
    filterName === "holdColors" &&
    isMultipleChoiceFilter(filterParamValue) &&
    filterParamValue.matches.size > 0
  ) {
    summary = (
      <>
        {Array.from(filterParamValue.matches).map((value) => (
          <Box
            key={value}
            sx={{
              width: 16,
              height: 16,
              backgroundColor: value,
              borderRadius: 8,
              border: "1px solid",
              borderColor: "#00000055",
              verticalAlign: "middle",
              marginLeft: -1,
              "&:first-of-type": {
                marginLeft: 0.5,
              },
            }}
          />
        ))}
      </>
    );
  }

  return (
    <Chip
      color="primary"
      size="sm"
      sx={{
        paddingX: 1.25,
        paddingY: 0.5,
        border: "1px solid",
        borderColor: "primary.blue2",
        color: "primary.blue2",
      }}
      endDecorator={
        <ChipDelete
          onClick={() => {
            removeFilterParam(filterName);
          }}
          sx={{ marginLeft: 0.25, color: "primary.blue2" }}
        >
          <Cancel />
        </ChipDelete>
      }
    >
      <Box sx={{ display: "flex", alignItems: "center" }}>
        {onlySummary ? (
          summary
        ) : (
          <>
            <span style={{ marginRight: 2 }}>{label}:</span> {summary}
          </>
        )}
      </Box>
    </Chip>
  );
};

export default function ActiveFiltersIndicator() {
  const filterParams = useFilterParams();
  const filterNames = Object.keys(filterParams) as (keyof typeof filterParams)[];
  const activeFilterNames = filterNames.filter(
    (name) => filterParams[name] !== undefined && name !== "searchQuery",
  );
  console.log("AFN", activeFilterNames);

  return (
    <Stack direction="row" gap={0.75}>
      {activeFilterNames.map((name) => (
        <FilterIndicator key={name} filterName={name} filterParamValue={filterParams[name]!} />
      ))}
    </Stack>
  );
}
